import { useState, useEffect } from "react";
import { Link } from "@/lib/router-compat";
import {
  ArrowRight,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  Instagram,
  ExternalLink,
} from "lucide-react";
import ResponsiveImage from "@/components/ResponsiveImage";
import { IMG } from "@/lib/optimized-images";

const FEATURE_SIZES =
  "(min-width: 1280px) 444px, (min-width: 1024px) 319px, (min-width: 768px) 359px, 362px";

const MAIN_CARDS = [
  {
    eyebrow: "QUALITY",
    title: "Triple ISO-Certified Craftsmanship",
    body: "Every light, every crevice, every texture - perfected. With our in-house joinery and manufacturing facility, we control quality at every stage, certified to ISO 9001, ISO 14001 and ISO 45001 standards.",
    linkLabel: "Explore Our Standards",
    linkHref: "/about-us#our-standards",
    rendition: IMG.iso,
    sizes: FEATURE_SIZES,
  },
  {
    eyebrow: "ON-TIME DELIVERY",
    title: "Zero Missed Deadlines in 24 Years",
    body: "Your opening date is sacred to us. Across hundreds of projects - from mall boutiques to bank headquarters - we have never delayed a handover. On time. Every time.",
    linkLabel: "View Our Projects",
    linkHref: "/projects",
    rendition: IMG.deadlines,
    sizes: FEATURE_SIZES,
  },
  {
    eyebrow: "ACCOUNTABILITY",
    title: "Your Brand Is Our Reputation",
    body: "We treat every project as if it were our own brand on the door. Promises given, promises delivered - no excuses. That's why our clients have been our only marketing team for 24 years.",
    linkLabel: "Read Our Story",
    linkHref: "/about-us",
    rendition: IMG.brand,
    sizes: FEATURE_SIZES,
  },
];

const ROTATING_FACTS = [
  "We've operated for 24 years without a single salesperson - our clients are our marketing team.",
  "We've never missed a handover deadline in 24 years.",
  "Our furniture, joinery, signage and even lighting are all manufactured in-house in Bahrain.",
  "You've probably walked through our work - Hugo Boss, Damas, illy Café, Burger King, Al Salam Bank, Technogym, and 180+ more brands across GCC.",
];

export default function NewsSection() {
  const [factIndex, setFactIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setFactIndex((prev) => (prev + 1) % ROTATING_FACTS.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const nextFact = () => {
    setFactIndex((prev) => (prev + 1) % ROTATING_FACTS.length);
  };

  const prevFact = () => {
    setFactIndex(
      (prev) => (prev - 1 + ROTATING_FACTS.length) % ROTATING_FACTS.length,
    );
  };

  return (
    <section
      className="py-20 md:py-28 bg-white border-b border-gray-200"
      id="why-abacus"
    >
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Section Header */}
        <div className="mb-14">
          <span className="text-[#002b5c] font-heading font-bold text-base md:text-lg lg:text-xl tracking-[0.25em] uppercase block mb-3">
            WHY ABACUS
          </span>
          <h2 className="text-[#002b5c] font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold uppercase tracking-tight mb-4">
            Three Reasons 180+ Brands Choose Abacus
          </h2>
          <p className="text-gray-600 font-body text-base md:text-lg max-w-3xl leading-relaxed">
            For 24 years, the world's leading brands have trusted us with their
            spaces - and keep coming back. Here's why.
          </p>
        </div>

        {/* Layout Grid: 8 columns main cards + 4 columns side cards */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Cards Grid (8 cols) */}
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            {MAIN_CARDS.map((card, i) => (
              <div
                key={i}
                className={`group bg-[#f8f9fa] border border-gray-200/80 rounded-xs overflow-hidden flex flex-col card-lift ${
                  i === 0 ? "md:col-span-2 flex-col md:flex-row" : ""
                }`}
              >
                <div
                  className={`overflow-hidden relative bg-[#002b5c] ${
                    i === 0
                      ? "md:w-1/2 aspect-video md:aspect-auto"
                      : "aspect-[4/3]"
                  }`}
                  style={{ minHeight: i === 0 ? "260px" : "200px" }}
                >
                  <ResponsiveImage
                    rendition={card.rendition}
                    alt={card.title}
                    sizes={card.sizes}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors" />
                  <div className="absolute top-4 left-4 sm:top-6 sm:left-6 bg-[#002b5c]/80 backdrop-blur-sm px-3 py-1 rounded-xs z-10">
                    <span className="text-[#ffcc29] font-heading font-bold text-xs tracking-widest uppercase">
                      {card.eyebrow}
                    </span>
                  </div>
                </div>

                <div
                  className={`p-6 sm:p-8 flex-1 flex flex-col justify-between ${i === 0 ? "md:w-1/2" : ""}`}
                >
                  <div>
                    <h3 className="text-[#002b5c] font-heading text-2xl font-bold mb-4 leading-tight group-hover:text-[#002b5c] transition-colors">
                      {card.title}
                    </h3>
                    <p className="text-gray-600 font-body text-sm leading-relaxed mb-6">
                      {card.body}
                    </p>
                  </div>

                  {card.linkHref.startsWith("/") ? (
                    <Link
                      to={card.linkHref}
                      className="mt-auto inline-flex items-center gap-2 text-[#002b5c] font-heading font-bold text-sm uppercase tracking-wider group/btn"
                    >
                      <span>{card.linkLabel}</span>
                      <ArrowRight className="w-4 h-4 text-[#ffcc29] transition-transform group-hover/btn:translate-x-1" />
                    </Link>
                  ) : (
                    <a
                      href={card.linkHref}
                      className="mt-auto inline-flex items-center gap-2 text-[#002b5c] font-heading font-bold text-sm uppercase tracking-wider group/btn"
                    >
                      <span>{card.linkLabel}</span>
                      <ArrowRight className="w-4 h-4 text-[#ffcc29] transition-transform group-hover/btn:translate-x-1" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Side Cards (4 cols) */}
          <div className="lg:col-span-4 flex flex-col gap-8">
            {/* Did You Know Box */}
            <div className="bg-[#ffcc29] p-8 text-[#002b5c] rounded-xs shadow-xs relative overflow-hidden flex flex-col justify-between min-h-[300px]">
              <div>
                <div className="flex items-center justify-between mb-6 pb-3 border-b border-[#002b5c]/15">
                  <div className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-[#002b5c]" />
                    <span className="font-heading font-bold text-sm md:text-base tracking-[0.2em] uppercase text-[#002b5c]">
                      DID YOU KNOW?
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={prevFact}
                      aria-label="Previous fact"
                      className="min-w-11 min-h-11 flex items-center justify-center rounded-sm text-[#002b5c]/70 hover:text-[#002b5c] hover:bg-[#002b5c]/10 transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={nextFact}
                      aria-label="Next fact"
                      className="min-w-11 min-h-11 flex items-center justify-center rounded-sm text-[#002b5c]/70 hover:text-[#002b5c] hover:bg-[#002b5c]/10 transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="min-h-[140px] sm:min-h-[160px] flex flex-col justify-center">
                  <p
                    className={`font-heading font-bold leading-snug mb-6 text-[#002b5c] ${
                      factIndex === 3
                        ? "text-xl sm:text-2xl"
                        : "text-2xl sm:text-3xl md:text-[1.75rem]"
                    }`}
                  >
                    "{ROTATING_FACTS[factIndex]}"
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-1 mb-6 -ml-2">
                  {ROTATING_FACTS.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setFactIndex(idx)}
                      aria-label={`Go to fact ${idx + 1}`}
                      className="min-w-11 min-h-11 flex items-center justify-center p-2 group"
                    >
                      <span
                        className={`h-1.5 rounded-full transition-all duration-300 block ${
                          idx === factIndex
                            ? "w-6 bg-[#002b5c]"
                            : "w-1.5 bg-[#002b5c]/40 group-hover:bg-[#002b5c]/70"
                        }`}
                      />
                    </button>
                  ))}
                </div>

                <a
                  href="#about"
                  className="inline-flex items-center gap-2 font-heading font-bold text-xs uppercase tracking-wider text-[#002b5c] border-b-2 border-[#002b5c] pb-1 hover:gap-3 transition-all"
                >
                  <span className="text-sm md:text-base">OUR STORY</span>
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Instagram Updates Card */}
            <div className="bg-gradient-to-br from-[#e1306c] via-[#fd1d1d] to-[#f77737] text-white p-6 sm:p-8 rounded-xs shadow-xs relative overflow-hidden group flex flex-col justify-between flex-1 min-h-[360px]">
              <Instagram className="absolute -right-6 -bottom-6 w-36 h-36 text-white/10 rotate-12 pointer-events-none" />

              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-white/20">
                  <Instagram className="w-5 h-5 text-white" />
                  <span className="text-white font-heading font-bold text-sm md:text-base tracking-[0.2em] uppercase">
                    INSTAGRAM UPDATES
                  </span>
                </div>

                <h4 className="font-heading text-xl sm:text-2xl font-bold mb-2 leading-tight">
                  Latest Posts & Behind-the-Scenes
                </h4>
                <p className="font-body text-sm sm:text-base lg:text-lg text-white/90 leading-relaxed mb-3">
                  Live project reveals, CNC joinery craftsmanship, and site
                  handovers across Bahrain.
                </p>

                {/* Instagram Native Embeds Grid */}
                <div className="grid grid-cols-2 gap-2 my-3">
                  {[
                    {
                      url: "https://www.instagram.com/p/DdrQ9sRD-bu/",
                      rendition: IMG.marble,
                      alt: "Marble in the CEO's Office - Abacus Interior Decoration",
                    },
                    {
                      url: "https://www.instagram.com/reel/DdjOxohlKmS/",
                      rendition: IMG.savanna,
                      alt: "From Concept to Reality - SAVANNA Flagship Store",
                    },
                    {
                      url: "https://www.instagram.com/p/Ddf5hw6G1OF/",
                      rendition: IMG.japandi,
                      alt: "Japandi Less but better reception counter - Abacus Interior Decoration",
                    },
                    {
                      url: "https://www.instagram.com/reel/DdOoxAngay7/",
                      rendition: IMG.hackett,
                      alt: "Hackett London store reveal - Abacus Interior Decoration",
                    },
                  ].map((post, idx) => (
                    <a
                      key={idx}
                      href={post.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group/thumb relative aspect-square rounded-sm overflow-hidden border border-white/40 shadow-xs block bg-black/20"
                    >
                      <ResponsiveImage
                        rendition={post.rendition}
                        alt={post.alt}
                        sizes="(min-width: 1024px) 177px, (min-width: 768px) 322px, 152px"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover/thumb:scale-110"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center">
                        <Instagram className="w-6 h-6 text-white" />
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              <a
                href="https://www.instagram.com/abacusinteriordecoration/"
                target="_blank"
                rel="noopener noreferrer"
                className="relative z-10 mt-3 inline-flex items-center gap-2 bg-white text-[#be185d] px-4 py-2.5 rounded-xs font-heading font-bold text-xs hover:bg-[#002b5c] hover:text-white transition-all uppercase tracking-wider w-full justify-center shadow-md"
              >
                <span>FOLLOW @ABACUSINTERIORDECORATION</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
